import { useState } from 'react';
import NavBar from '../navbar/navbar';
import { H2, Button, BackIconGreen } from '../reusable-components';
import { useTranslation } from "react-i18next";
import { useParams, useHistory, useLocation } from 'react-router-dom';
import useDetailRound from '../rounds/useDetailRound';
import SyndicateConfirm from './syndicateconfirm';
import Moment from "react-moment";
import { usePlayer } from "../player/playerContext";
import { Redirect } from "react-router-dom";
import { getReducedFiendlyName, getRoundName } from '../common/RoundHelper';
import { PoolXNumberFormat } from '../common/CurrencyFormatter';
import SyndicateCoupon from './syndicatecoupon';
import { ClickAwayListener, Grid, IconButton, Tooltip, Typography, SxProps, Theme } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { IsLeo, IsVideo, themeC } from '../theme/themehelper';
import { operatorAdviceLogin, operatorAdviceScrollToTop } from '../App';
import useSyndicate from './useSyndicate';
import LoginDialog from '../slimcoupon/logindialog';
import { trackBeginCheckout } from '../analytics/analytics';

export default function Syndicate() {
  const { syndicateId } = useParams<{ syndicateId: string }>();
  const { t } = useTranslation();
  const [showConfirm, setShowConfirm] = useState(false);
  const [player, config] = usePlayer() as any;
  let [showLoginDialog, setShowLoginDialog] = useState(false);
  const history = useHistory();
  const location = useLocation();
  const hasAppHistory = !!(location as any).key;

  const goBack = () => {
    if (hasAppHistory) {
      history.goBack();
    } else {
      history.push("/clear");
    }
  };

  const syndicateResponse = useSyndicate(Number(syndicateId));
  const { round } = useDetailRound(syndicateResponse.syndicate?.roundId);

  const [stakeMultiplierTooltipOpen, setStakeMultiplierTooltipOpen] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const hideMenu = urlParams.get("hideMenu") === 'true';

  if (syndicateResponse.error || syndicateResponse.syndicate === undefined) {
    return null;
  }

  const syndicate = syndicateResponse.syndicate;

  const handleStakeMultiplierTooltipClose = () => {
    setStakeMultiplierTooltipOpen(false);
  };

  const handleStakeMultiplierTooltipOpen = () => {
    setStakeMultiplierTooltipOpen(true);
  };

  const handleLoginClick = () => {

    setShowLoginDialog(false);

    let loginEvent = {
      type: "login_user",
      navigationId: syndicate.id,
      navigationKey: "syndicate",
      roundId: syndicate.roundId
    }
    operatorAdviceLogin(loginEvent);
  }

  if (!syndicate) {
    return <Redirect to={"/clear"} />
  }

  if (!round) return null;

  let theme = window.theme;
  const isGreenTheme = theme === themeC.Green;

  let showLogin = IsLeo(theme);

  if (IsVideo(theme) && player._links.self.href.indexOf('demo') > -1) {
    showLogin = true;
  }

  const openToDate = new Date(round.openTo);
  let locale = t('locale');

  const nf = PoolXNumberFormat(locale, { style: 'currency', currency: syndicate.stakePerShare.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 });
  let formattedStakePerShare = nf.format(syndicate.totalAmountPerShare.amount);
  let feePercentage = Math.round((syndicate.agentFeePerShare.amount / syndicate.stakePerShare.amount * 100)) + "%";

  if (!(config.syndicateBetting || config.agentSyndicateBetting)) {
    return <div />;
  }

  let isRealMode = player && player._embedded && player._embedded.mode === 'real';

  const handlePayClick = () => {

    if (showLogin && !isRealMode) {
      setShowLoginDialog(true);
      operatorAdviceScrollToTop(500);
    }
    else {
      trackBeginCheckout({
        round_id: syndicate.roundId,
        value: syndicate.totalAmountPerShare.amount,
        currency: syndicate.totalAmountPerShare.currency,
        system_type: "SYNDICATE",
      });
      setShowConfirm(true);
      operatorAdviceScrollToTop(500);
    }
  }

  const handleToggle = (toggle: boolean) => {
    setShowConfirm(toggle);
    if (window.isTerminal) {
      history.goBack();
    }
  }

  let rowStyle: SxProps<Theme> = {
    background: 'var(--background_1)',
    alignItems: 'center',
    paddingTop: '30px',
    paddingBottom: '10px',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    width: "auto",
    padding: "5px"
  }

  let textStyle: SxProps<Theme> = {
    fontWeight: 'bold',
  }

  let wrapperStyle: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    fontSize: '15px',
    paddingBottom: '120px',
  }

  let greenDivColumnStyle: SxProps<Theme> = {
    background: 'var(--background_1)',
    display: 'flex',
    alignItems: 'center',
    paddingTop: '42px',
    paddingBottom: '10px',
    flexDirection: 'column'    
  }

  let whiteDivStyle: SxProps<Theme> = {
    background: 'var(--background_2)',
    display: 'flex',
    alignItems: 'center',
    paddingTop: '42px',
    paddingBottom: '0px',
    marginTop: '12px',
    flexDirection: 'column',
  }

  let titleMarginStyle: SxProps<Theme> = {
    marginBottom: '10px',
  }

  let titleUnderStyle: SxProps<Theme> = {
    fontWeight: 'bold',
  }

  let isReduced = false;
  let isSingle = false;
  let reducedSystemName = "";

  if (!syndicate.couponHidden || syndicate.showDraftCoupon) {
    isReduced = syndicate.pendingRoundCoupon.bets[0].system.type === "REDUCED";
    isSingle = syndicate.pendingRoundCoupon.bets.length > 1;
    if (isReduced) {
      reducedSystemName = getReducedFiendlyName(syndicate.pendingRoundCoupon.bets[0].system.reducedSystemId)
    }

  }

  return (
    <>
      {!hideMenu && <NavBar whichToUnderline="2" balanceUpdateId="0" />}
      {isGreenTheme && (
        <Grid sx={{ margin: '10px 0px 0px 16px', cursor: 'pointer' }} onClick={goBack}>
          {BackIconGreen()}
        </Grid>
      )}
      {showLoginDialog && (
        <LoginDialog open={showLoginDialog} onClose={() => handleLoginClick()} />
      )}
      <Grid sx={wrapperStyle} container direction="column">
        <Grid sx={greenDivColumnStyle}>
          <H2 sx={titleMarginStyle}>{syndicate.name}</H2>
          <Typography sx={titleUnderStyle}>{getRoundName(round, t)} <Moment format={"L"} locale={locale}>{openToDate}</Moment></Typography>
          {syndicate.agentUserName !== undefined && <Typography sx={rowStyle}>{syndicate.agentUserName}</Typography>}
        </Grid>
        {syndicate.description &&
          <Typography sx={{ maxWidth: "340px", margin: "20px" }}>{syndicate.description}</Typography>
        }

        {isReduced &&
          <Grid container direction="row" justifyContent="center">
            <Grid container sx={rowStyle}>
              <ClickAwayListener onClickAway={handleStakeMultiplierTooltipClose}>
                <div>
                  <Tooltip
                    PopperProps={{
                      disablePortal: true,
                    }}
                    onClose={handleStakeMultiplierTooltipClose}
                    open={stakeMultiplierTooltipOpen}
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    placement="top-end"
                    title={t('reduced_disclaimer')}
                  >
                    <IconButton onClick={handleStakeMultiplierTooltipOpen}>
                      <InfoOutlinedIcon sx={{ fontSize: "15px", color: 'var(--off-black)' }} height="20" width="20" />
                    </IconButton>
                  </Tooltip>
                </div>
              </ClickAwayListener>
              <Typography>{t('reduced_system')}:</Typography>
              <Typography sx={textStyle}>{reducedSystemName} </Typography>
            </Grid>
          </Grid>
        }
        {isSingle &&
          <Grid container direction="row" justifyContent="center">
            <Grid container sx={rowStyle}>
              <Typography>{t('single_rows')}</Typography>
            </Grid>
          </Grid>
        }
        <Grid sx={whiteDivStyle}>
          {showConfirm && (
            <SyndicateConfirm
              toggle={(toggle: boolean) => handleToggle(toggle)}
              syndicate={syndicate}
              purchase={syndicateResponse.purchase}
            />
          )}

          <Button margin="12px 0px"
            disabled={(!isRealMode && !showLogin) || syndicate.nShares === syndicate.targetNShares}
            onClick={() => handlePayClick()}>
            {t('pay_syndicate') + ' ' + formattedStakePerShare}
          </Button>

          <SyndicateCoupon syndicate={syndicate} round={round} theme={theme}></SyndicateCoupon>
        </Grid>

        <Grid container sx={{marginTop: "12px"}} direction="row" justifyContent="center">
          <Grid container sx={rowStyle}>
            <Typography>{t('share_price')}:</Typography>
            <Typography sx={textStyle}>{formattedStakePerShare} </Typography>
          </Grid>
          <Grid container sx={rowStyle}>
            <Typography>{t('shares')}:</Typography>
            <Typography sx={textStyle}>{syndicate.targetNShares} </Typography>
          </Grid>
        </Grid>
        <Grid container direction="row" justifyContent="center">
          {!syndicate.nSharesHidden &&
            <Grid container sx={rowStyle}>
              <Typography>{t('sold_shares')}:</Typography>
              <Typography sx={textStyle}>{syndicate.nShares} </Typography>
            </Grid>
          }
          <Grid container sx={rowStyle}>
            <Typography>{t('fee')}:</Typography>
            <Typography sx={textStyle}>{feePercentage}</Typography>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
}